import { CurrencyAmount } from '~/components/component-library'
import {
  TokenBalance,
  TokenBalanceProps,
} from '~/components/component-library/TokenBalance'
import {
  AssetType,
  BasicTransactionType,
  IAddress,
  TransferType,
} from '~/types'
import { cn } from '~/utils/tailwind-utils'
import { isAutoEarnTransfer } from '~/utils/vault'

export const getDirection = (
  transaction: TransactionForValue,
): TokenBalanceProps['direction'] => {
  if (!transaction.isSplitsTransaction) {
    if (
      transaction.to &&
      transaction.tokenAddress &&
      isAutoEarnTransfer({
        chainId: transaction.chainId,
        transferType: transaction.transferType,
        tokenAddress: transaction.tokenAddress,
        to: transaction.to,
      })
    ) {
      return 'neutral'
    }

    return transaction.transferType === TransferType.Inbound
      ? 'inflow'
      : 'outflow'
  }

  if (
    transaction.isSplitsTransaction &&
    !transaction.isPublicTransaction &&
    transaction.isOfframp
  ) {
    return 'outflow'
  }

  const types = transaction.types as BasicTransactionType[]
  const isTransfer = types.includes(BasicTransactionType.Transfer)
  const isSwap = types.includes(BasicTransactionType.Swap)
  const isBridge = types.includes(BasicTransactionType.Bridge)
  const isClaim = types.includes(BasicTransactionType.Claim)
  const isCustomLike =
    types.includes(BasicTransactionType.Batch) ||
    types.includes(BasicTransactionType.CustomTxn) ||
    types.includes(BasicTransactionType.WalletConnect) ||
    types.includes(BasicTransactionType.SplitsConnect)

  if (isTransfer) {
    return 'outflow'
  }

  if (isSwap) {
    return isBridge ? 'outflow' : 'neutral'
  }

  if (isBridge) {
    return 'outflow'
  }

  if (isClaim) {
    return 'inflow'
  }

  if (isCustomLike) {
    // If the display value is large but net value is near zero,
    // the transaction is balanced (swap-like) — show as neutral.
    if (transaction.usdDisplayValue && transaction.usdNetValue) {
      const displayAbs = Math.abs(Number(transaction.usdDisplayValue))
      const netAbs = Math.abs(Number(transaction.usdNetValue))
      if (displayAbs > 0 && netAbs / displayAbs <= 0.1) {
        return 'neutral'
      }
    }

    if (transaction.usdDisplayValue) {
      const raw = transaction.usdDisplayValue.trim()
      const isParenNeg = raw.startsWith('(') && raw.endsWith(')')
      const cleaned = raw.replace(/[^0-9.\-]/g, '')
      const numericValue = Number(cleaned) * (isParenNeg ? -1 : 1)
      if (!Number.isNaN(numericValue) && numericValue !== 0) {
        return numericValue > 0 ? 'inflow' : 'outflow'
      }
    }
  }

  return 'neutral'
}

export type TransactionForValue =
  | {
      chainId: number
      isSplitsTransaction: true
      displayTokenAddress?: string | null
      displayTokenAmount?: string | null
      usdDisplayValue?: string
      usdNetValue?: string | null
      displayChainId?: number | null
      types: BasicTransactionType[]
      isOfframp?: boolean
      isPublicTransaction?: boolean
    }
  | {
      chainId: number
      isSplitsTransaction: false
      transferType: (typeof TransferType)[keyof typeof TransferType]
      assetType: AssetType
      amount?: string | null
      tokenAddress?: string
      to?: string
      value?: string | null
    }

export const TransactionValue = ({
  transaction,
  className,
  isSmall = false,
  showNetworkIcon = false,
  layout = 'standard',
}: {
  transaction: TransactionForValue
  className?: string
  isSmall?: boolean
  showNetworkIcon?: boolean
  layout: 'standard' | 'two-line'
}) => {
  if (!transaction.chainId) {
    return <></>
  }

  let tokenAmount = '0'
  let tokenAddress: IAddress | undefined
  let usdValue: string | undefined
  let chainId = transaction.chainId

  if (transaction.isSplitsTransaction) {
    if (transaction.displayTokenAddress) {
      tokenAddress = transaction.displayTokenAddress as IAddress
    }

    tokenAmount = transaction.displayTokenAmount || '0'
    usdValue = transaction.usdDisplayValue
    chainId = transaction.displayChainId || transaction.chainId
  } else if (
    transaction.assetType === 'ERC1155' ||
    transaction.assetType === 'ERC721'
  ) {
    return <></>
  } else {
    tokenAmount = transaction.amount || '0'
    tokenAddress = transaction.tokenAddress as IAddress
    usdValue = transaction.value ?? undefined
  }

  const numberUsdValue = usdValue ? Number(usdValue) : undefined

  if (!tokenAddress) {
    if (!numberUsdValue) return <></>

    return (
      <div
        className={cn(
          'flex max-w-44 flex-col items-end justify-center',
          layout === 'two-line' && 'gap-0.5',
        )}
      >
        <CurrencyAmount
          value={numberUsdValue}
          direction={getDirection(transaction)}
          size={isSmall ? 'small' : 'base'}
          layout="standard"
        />
        {layout === 'two-line' && (
          <div
            className={cn(
              'text-secondary',
              isSmall ? 'h-3 text-xs leading-4' : 'h-4 text-sm leading-4',
            )}
          >
            &nbsp;
          </div>
        )}
      </div>
    )
  }

  const direction = getDirection(transaction)

  return (
    <div className={cn(className, 'w-full pl-6')}>
      <TokenBalance
        address={tokenAddress}
        chainId={chainId}
        tokenAmount={BigInt(tokenAmount)}
        usdValue={numberUsdValue}
        layout="standard"
        size={isSmall ? 'small' : 'base'}
        direction={direction}
        showNetworkIcon={showNetworkIcon}
      />
    </div>
  )
}
